/**
 * Observability API client — queries Mimir/Loki/Tempo via analytics-service proxy.
 *
 * The analytics-service exposes /v1/observability/* which forwards to the
 * LGTM backends in the monitoring namespace. We use the existing analytics
 * HTTP client (ky-based) but target the observability prefix.
 */

import ky, { type KyInstance } from 'ky'
import { handleUnauthorized } from '@agion/auth'
import { getConfig } from '@/core/analytics_runtime_env'
import { keysToCamel, keysToSnake } from '@/core/analytics_utils'
import type { PromQLResponse, LokiQueryResponse, LokiLabelValuesResponse, AnalyzeLogRequest, AnalyzeLogResponse } from '@/core/observability_types'

const GATEWAY_URL = getConfig('VITE_API_URL', 'http://localhost:8090') || '/api'

const obsClient: KyInstance = ky.create({
  prefix: `${GATEWAY_URL}/v1/observability`,
  // Preserve Ky 1's successful empty-body result for command endpoints.
  parseJson: text => text === '' ? '' : JSON.parse(text),
  timeout: 30000,
  credentials: 'include',
  retry: {
    limit: 2,
    methods: ['get'],
    statusCodes: [502, 503, 504],
  },
  hooks: {
    beforeRequest: [
      ({ request }) => {
        request.headers.set('Accept', 'application/json')
        // SECURITY: X-Organization-ID is injected by APISIX forward-auth
        // from the HttpOnly session cookie. No client-side header needed.
        // Removed localStorage read — XSS could forge org context.
      }
    ],
    beforeError: [
      ({ error }) => {
        if ('response' in error && (error as { response: Response }).response?.status === 401) {
          // Soft-401: probe canonical session state before redirecting;
          // only a confirmed-dead session bounces the user to login.
          void handleUnauthorized()
        }
        return error
      }
    ]
  }
})

/** Instant PromQL query */
export async function queryInstant(query: string, signal?: AbortSignal): Promise<PromQLResponse> {
  return obsClient.get('query', { searchParams: { query }, signal }).json<PromQLResponse>()
}

/** Range PromQL query */
export async function queryRange(
  query: string,
  start: string,
  end: string,
  step: string,
  signal?: AbortSignal,
): Promise<PromQLResponse> {
  return obsClient.get('query_range', {
    searchParams: { query, start, end, step },
    signal,
  }).json<PromQLResponse>()
}

/** Instant PromQL query against the environment's registered GPU metrics source. */
export async function queryGpuInstant(query: string, signal?: AbortSignal): Promise<PromQLResponse> {
  return obsClient.get('gpu/query', { searchParams: { query }, signal }).json<PromQLResponse>()
}

/** Range PromQL query against the environment's registered GPU metrics source. */
export async function queryGpuRange(
  query: string,
  start: string,
  end: string,
  step: string,
  signal?: AbortSignal,
): Promise<PromQLResponse> {
  return obsClient.get('gpu/query_range', {
    searchParams: { query, start, end, step },
    signal,
  }).json<PromQLResponse>()
}

/** Get label values (e.g., service names) */
export async function labelValues(labelName: string): Promise<PromQLResponse> {
  return obsClient.get(`label/${labelName}/values`).json<PromQLResponse>()
}

/** LogQL range query */
export async function queryLogs(
  query: string,
  start: string,
  end: string,
  limit = 100
): Promise<LokiQueryResponse> {
  return obsClient.get('logs', {
    searchParams: { query, start, end, limit: String(limit) }
  }).json<LokiQueryResponse>()
}

/** LogQL label names */
export async function logLabels(): Promise<LokiQueryResponse> {
  return obsClient.get('logs/labels').json<LokiQueryResponse>()
}

/** Loki label values (e.g., service names for 'app' label) */
export async function logLabelValues(labelName: string): Promise<LokiLabelValuesResponse> {
  return obsClient.get(`logs/label/${labelName}/values`).json<LokiLabelValuesResponse>()
}

/** Analyze a log entry using LLM */
export async function analyzeLogEntry(req: AnalyzeLogRequest): Promise<AnalyzeLogResponse> {
  const response = await obsClient.post('logs/analyze', { json: keysToSnake(req) }).json<unknown>()
  return keysToCamel<AnalyzeLogResponse>(response)
}
